import { useMutation } from "@tanstack/react-query";
import { useId, useState } from "react";
import { Link, useSearchParams } from "react-router";

import { AuthLayout } from "@/components/layout";
import {
  Alert,
  Button,
  buttonClass,
  ErrorAlert,
  Field,
  Form,
  PasswordInput,
  PasswordRequirements,
} from "@/components/ui";
import { auth } from "@/lib/api";
import { MAX_PASSWORD, meetsPasswordPolicy } from "@/lib/password-policy";
import { passwordError } from "@/pages/auth/helpers";

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [password, setPassword] = useState("");
  const requirementsId = useId();
  const reset = useMutation({ mutationFn: () => auth.resetPassword(token, password) });
  if (!token) {
    return (
      <AuthLayout title="Reset link invalid">
        <Alert tone="danger">This link is incomplete. Request a new one.</Alert>
        <Link to="/forgot-password" className="mt-4 inline-block text-sm text-brand-700 hover:underline">
          Request a new link
        </Link>
      </AuthLayout>
    );
  }
  const pwErr = passwordError(reset.error);
  // The link carries no e-mail, so Identity's "doesn't contain your e-mail" rule is only checked on submit.
  const passwordOk = meetsPasswordPolicy(password);
  return (
    <AuthLayout title="Choose a new password">
      {reset.isSuccess ? (
        <div className="space-y-4">
          <Alert tone="success">Your password was changed. Other sessions were signed out.</Alert>
          <Link to="/login" className={buttonClass("primary", "md", "w-full")}>
            Sign in
          </Link>
        </div>
      ) : (
        <Form
          className="space-y-4"
          error={reset.error}
          onSubmit={(e) => {
            e.preventDefault();
            if (passwordOk) reset.mutate();
          }}
        >
          {reset.error && !pwErr && <ErrorAlert error={reset.error} />}
          <Field label="New password" name="new_password" required error={pwErr}>
            {(p) => (
              <>
                <PasswordInput
                  {...p}
                  aria-describedby={[p["aria-describedby"], requirementsId].filter(Boolean).join(" ")}
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  maxLength={MAX_PASSWORD}
                  // eslint-disable-next-line jsx-a11y/no-autofocus -- the form is this screen's only task, so focus starts in its first field (PHP-3348)
                  autoFocus
                />
                <PasswordRequirements id={requirementsId} password={password} />
              </>
            )}
          </Field>
          <Button type="submit" className="w-full" loading={reset.isPending} disabled={!passwordOk}>
            Change password
          </Button>
        </Form>
      )}
    </AuthLayout>
  );
}
